import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { OnboardingService } from '../../generated/auth/services/onboarding.service';
import * as ProposalActions from './proposal.actions';
import { ProposalLoadParams } from './proposal.model';
import { IProposalState } from './proposal.state';

@Injectable()
export class ProposalEffects {
  private actions$ = inject(Actions);
  private onboardingService = inject(OnboardingService);
  private store = inject(Store<{ proposal: IProposalState }>);

  constructor() {}

  loadProposalsForReview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalActions.loadProposalsForReview),
      switchMap(() =>
        this.onboardingService.onboardingProposalGetProposalsForReviewGet().pipe(
          map((response) => ProposalActions.loadProposalsSuccess({ response })),
          catchError((error) => {
            const errorMessage =
              error?.error?.message || error?.message || 'Error loading proposals';
            return of(ProposalActions.loadProposalsFailure({ error: errorMessage }));
          }),
        ),
      ),
    ),
  );

  loadProposalsByCreator$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalActions.loadProposalsByCreator),
      withLatestFrom(this.store.select((state) => state.proposal)),
      switchMap(([action, state]) => {
        const filters = state.filters;
        const params: ProposalLoadParams = {
          ...filters,
          page: action.params?.Page || filters.page,
          pageSize: action.params?.PageSize || filters.pageSize,
        };

        // Clean undefined values from params
        const cleanParams: any = {};
        Object.keys(params).forEach((key) => {
          if (params[key as keyof ProposalLoadParams] !== undefined) {
            cleanParams[key] = params[key as keyof ProposalLoadParams];
          }
        });

        return this.onboardingService.onboardingProposalGetCreatorProposalsGet(cleanParams).pipe(
          map((response) =>
            ProposalActions.loadProposalsByCreatorSuccess({
              response,
              currentPage: cleanParams.page || 1,
            }),
          ),
          catchError((error) =>
            of(
              ProposalActions.loadProposalsByCreatorFailure({
                error: error.message || 'Unknown error',
              }),
            ),
          ),
        );
      }),
    ),
  );

  reloadOnFiltersChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ProposalActions.setFilter,
        ProposalActions.resetFilter,
        ProposalActions.updateFilter,
        ProposalActions.updatePagination,
        ProposalActions.setSorting,
      ),
      map(() => ProposalActions.loadProposalsByCreator({})),
    ),
  );

  loadMyProposals$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalActions.loadMyProposals),
      switchMap(() =>
        this.onboardingService.onboardingProposalGetProposalsGet().pipe(
          map((response) => ProposalActions.loadMyProposalsSuccess({ response })),
          catchError((error) => {
            const errorMessage =
              error?.error?.message || error?.message || 'Error loading my proposals';
            return of(ProposalActions.loadMyProposalsFailure({ error: errorMessage }));
          }),
        ),
      ),
    ),
  );

  acceptProposal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalActions.acceptProposal),
      switchMap(({ params }) =>
        this.onboardingService.onboardingProposalAcceptProposalPost(params).pipe(
          map((response) => ProposalActions.acceptProposalSuccess({ response })),
          catchError((error) => {
            const errorMessage =
              error?.error?.message || error?.message || 'Error accepting proposal';
            return of(ProposalActions.acceptProposalFailure({ error: errorMessage }));
          }),
        ),
      ),
    ),
  );

  declineProposal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalActions.declineProposal),
      switchMap(({ params }) =>
        this.onboardingService.onboardingProposalDeclineProposalPost(params).pipe(
          map((response) => ProposalActions.declineProposalSuccess({ response })),
          catchError((error) => {
            const errorMessage =
              error?.error?.message || error?.message || 'Error declining proposal';
            return of(ProposalActions.declineProposalFailure({ error: errorMessage }));
          }),
        ),
      ),
    ),
  );

  loadProposalById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalActions.loadProposalById),
      switchMap(({ id }) =>
        this.onboardingService.onboardingProposalGetProposalWithDocumentByIdGet({ id }).pipe(
          map((response) => ProposalActions.loadProposalByIdSuccess({ response })),
          catchError((error) => {
            const errorMessage =
              error?.error?.message || error?.message || 'Error loading proposal';
            return of(ProposalActions.loadProposalByIdFailure({ error: errorMessage }));
          }),
        ),
      ),
    ),
  );

  getProposalDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProposalActions.getProposalDocument),
      switchMap(({ documentId }) =>
        this.onboardingService.onboardingSasfinDocumentDownloadDocumentGet$Pdf({ documentId }).pipe(
          map((document) => ProposalActions.getProposalDocumentSuccess({ document })),
          catchError((error) => {
            const errorMessage =
              error?.error?.message || error?.message || 'Error getting proposal document';
            return of(ProposalActions.getProposalDocumentFailure({ error: errorMessage }));
          }),
        ),
      ),
    ),
  );
}
